import { getSearchParam } from "@/utils";
import { useHostName } from "./useHostName";

export const TRAN_TAB_AD_HOST = "tr-tab.comfortweather.com";
export const WWW_AD_HOST = "www.comfortweather.com";
export const TRAN_TAB_CHANNEL = "tran_ls_tab";
export const WWW_TRAN_TAB_AD_CONFIG_HOST = "www-tran-ls-tab.comfortweather.com";

/**
 * 回滚开关：
 * - 关闭 `ENABLE_TRAN_AD_HOST_REDIRECT`：`www+tran_ls_tab` 恢复按当前 host 读取广告配置。
 * - 关闭 `ENABLE_TRAN_AD_GESTURE_GATE`：`tr-tab` 恢复首屏即加载广告脚本。
 */
export const ENABLE_TRAN_AD_HOST_REDIRECT = true;
export const ENABLE_TRAN_AD_GESTURE_GATE = true;

export interface AdScenarioResult {
  hostName: string;
  channel: string;
  isTranTabHost: boolean;
  isTranTabChannelScenario: boolean;
  isTranTabAdScenario: boolean;
  effectiveAdHost: string;
  shouldGestureGateAds: boolean;
}

const normalize = (value: unknown): string => String(value || "").trim().toLowerCase();

/**
 * 统一广告场景解析：
 * - `isTranTabAdScenario`：`tr-tab` 与 `www+tran_ls_tab` 广告策略统一入口。
 * - `effectiveAdHost`：命中 tran 场景后，广告配置 key 统一指向 `tr-tab`。
 * - `shouldGestureGateAds`：仅真实 `tr-tab` host 启用手势门控。
 */
export function resolveAdScenario(input: { hostName?: string; channel?: string }): AdScenarioResult {
  const hostName = normalize(input.hostName);
  const channel = normalize(input.channel);
  const isTranTabHost = hostName === TRAN_TAB_AD_HOST;
  const isTranTabChannelScenario = hostName === WWW_AD_HOST && channel === TRAN_TAB_CHANNEL;
  const isTranTabAdScenario = isTranTabHost || isTranTabChannelScenario;
  /**
   * 说明：
   * - 真实 `tr-tab` 仍继续复用原来的广告配置。
   * - `www + tran_ls_tab` 单独切到新的 ADX 配置 key，避免误伤参考域名。
   */
  const effectiveAdHost =
    ENABLE_TRAN_AD_HOST_REDIRECT && isTranTabAdScenario
      ? isTranTabChannelScenario
        ? WWW_TRAN_TAB_AD_CONFIG_HOST
        : TRAN_TAB_AD_HOST
      : hostName;

  return {
    hostName,
    channel,
    isTranTabHost,
    isTranTabChannelScenario,
    isTranTabAdScenario,
    effectiveAdHost,
    shouldGestureGateAds: ENABLE_TRAN_AD_GESTURE_GATE && isTranTabHost,
  };
}

export function getCurrentAdScenario(): AdScenarioResult {
  const { hostName } = useHostName();
  const channel = getSearchParam("channel") || "";
  return resolveAdScenario({ hostName, channel });
}

/**
 * 运行时最小自检，防止后续改动误伤场景映射。
 */
export function runAdScenarioSanityChecks(): boolean {
  const cases = [
    {
      input: { hostName: "tr-tab.comfortweather.com", channel: "" },
      expect: {
        isTranTabAdScenario: true,
        effectiveAdHost: TRAN_TAB_AD_HOST,
        shouldGestureGateAds: ENABLE_TRAN_AD_GESTURE_GATE,
      },
    },
    {
      input: { hostName: "www.comfortweather.com", channel: "tran_ls_tab" },
      expect: {
        isTranTabAdScenario: true,
        effectiveAdHost: ENABLE_TRAN_AD_HOST_REDIRECT ? WWW_TRAN_TAB_AD_CONFIG_HOST : WWW_AD_HOST,
        shouldGestureGateAds: false,
      },
    },
    {
      input: { hostName: "www.comfortweather.com", channel: "v_ls_disnews" },
      expect: { isTranTabAdScenario: false, effectiveAdHost: WWW_AD_HOST, shouldGestureGateAds: false },
    },
  ];

  return cases.every(item => {
    const result = resolveAdScenario(item.input);
    return (
      result.isTranTabAdScenario === item.expect.isTranTabAdScenario &&
      result.effectiveAdHost === item.expect.effectiveAdHost &&
      result.shouldGestureGateAds === item.expect.shouldGestureGateAds
    );
  });
}
